'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

interface CFXUser {
  id: number;
  username: string;
  cfxId: string;
  role: string;
  lastLogin: string | null;
}

interface CFXAuthContextType {
  user: CFXUser | null;
  loading: boolean;
  login: (returnUrl?: string) => void;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const CFXAuthContext = createContext<CFXAuthContextType | undefined>(undefined);

export function CFXAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CFXUser | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/cfx/me', {
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (returnUrl?: string) => {
    const url = returnUrl 
      ? `/api/auth/cfx/login?redirect=${encodeURIComponent(returnUrl)}`
      : '/api/auth/cfx/login';
    
    window.location.href = url;
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/cfx/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      setUser(null);
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      setUser(null);
      window.location.href = '/login';
    }
  };

  return (
    <CFXAuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      isAuthenticated,
    }}>
      {children}
    </CFXAuthContext.Provider>
  );
}

export function useCFXAuth() {
  const context = useContext(CFXAuthContext);
  if (context === undefined) {
    throw new Error('useCFXAuth must be used within a CFXAuthProvider');
  }
  return context;
}
